/* Optional page-only companion. The shared review renderer remains the data owner. */
(() => {
  'use strict';
  const bootKey = '__sdResourceReviewsPaginationV1';
  if (window[bootKey] || typeof MutationObserver !== 'function') return;
  const mainSelector = 'main[data-template="page.sd-resource-reviews"]';
  const wrapperSelector = '[id^="shopify-section-template--"][id$="__sd_reviews_all_page"]';
  const feedSelector = '.sd-native-reviews[data-review-mode="all"]';
  const cardSelector = '.sd-native-review-card';
  const pageSize = 12;
  const managers = new Map();
  window[bootKey] = true;

  function labels() {
    const config = document.getElementById('sd-resource-reviews-pagination-labels');
    if (!config) return null;
    const value = JSON.parse(config.textContent);
    return ['navigation', 'previous', 'next', 'showAll', 'showPages', 'pageStatus', 'allStatus']
      .every(key => typeof value[key] === 'string' && value[key].trim()) ? value : null;
  }

  function format(template, values) {
    return template.replace(/\[\[(\w+)\]\]/g, (match, key) => values[key] ?? match);
  }

  function attach(main, wrapper, copy) {
    let track = null;
    let cards = [];
    let nav = null;
    let status = null;
    let previous = null;
    let next = null;
    let toggle = null;
    let ownTrackId = false;
    let page = 0;
    let showAll = false;
    let failed = false;
    let listeners = null;

    function restore() {
      const ownedFocus = !!nav && nav.contains(document.activeElement);
      // Restore even detached original nodes; never delete, clone, sort or replace a card.
      for (const card of cards) {
        if (card.hasAttribute('data-sd-rp-hidden')) {
          card.removeAttribute('hidden');
          card.removeAttribute('data-sd-rp-hidden');
        }
      }
      if (track) {
        track.removeAttribute('data-sd-rp-active');
        if (ownTrackId) track.removeAttribute('id');
      }
      if (listeners) listeners.abort();
      if (nav) nav.remove();
      if (ownedFocus && track && track.isConnected) {
        const previousTabindex = track.getAttribute('tabindex');
        track.setAttribute('tabindex', '-1');
        track.focus();
        if (previousTabindex === null) track.removeAttribute('tabindex');
        else track.setAttribute('tabindex', previousTabindex);
      }
      nav = status = previous = next = toggle = listeners = null;
      ownTrackId = false;
    }

    function failOpen() {
      failed = true;
      observer.disconnect();
      restore();
    }

    function render(moveFocus = false) {
      // Do not hide anything if the companion stylesheet is unavailable or removed.
      if (getComputedStyle(nav).getPropertyValue('--sd-rp-ready').trim() !== '1') {
        throw new Error('Pagination styles unavailable');
      }
      const pages = Math.ceil(cards.length / pageSize);
      page = Math.max(0, Math.min(page, pages - 1));
      const start = page * pageSize;
      const end = Math.min(start + pageSize, cards.length);
      previous.disabled = showAll || page === 0;
      next.disabled = showAll || page === pages - 1;
      toggle.textContent = showAll ? copy.showPages : copy.showAll;
      status.textContent = format(showAll ? copy.allStatus : copy.pageStatus, {
        page: page + 1, pages, start: start + 1, end, total: cards.length
      });
      track.setAttribute('data-sd-rp-active', 'true');
      for (let index = 0; index < cards.length; index += 1) {
        const hide = !showAll && (index < start || index >= end);
        if (hide) {
          cards[index].setAttribute('data-sd-rp-hidden', 'true');
          cards[index].hidden = true;
        } else if (cards[index].hasAttribute('data-sd-rp-hidden')) {
          cards[index].removeAttribute('hidden');
          cards[index].removeAttribute('data-sd-rp-hidden');
        }
      }
      // Status is immediately before controls, so Tab continues through enabled controls
      // into the current cards. Native buttons supply Enter/Space activation.
      if (moveFocus) status.focus();
    }

    function makeControls() {
      nav = document.createElement('nav');
      nav.className = 'sd-review-pagination';
      nav.setAttribute('aria-label', copy.navigation);
      status = document.createElement('p');
      status.className = 'sd-review-pagination__status';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      status.tabIndex = -1;
      nav.append(status);
      const actions = document.createElement('div');
      actions.className = 'sd-review-pagination__actions';
      nav.append(actions);
      if (!track.id) {
        const id = wrapper.id + '--sd-review-pagination-track';
        if (document.getElementById(id)) throw new Error('Track ID collision');
        track.id = id;
        ownTrackId = true;
      }
      listeners = new AbortController();
      function button(text, action) {
        const control = document.createElement('button');
        control.type = 'button';
        control.className = 'sd-review-pagination__button';
        control.textContent = text;
        control.setAttribute('aria-controls', track.id);
        control.addEventListener('click', () => {
          try { action(); render(true); } catch (_) { failOpen(); }
        }, { signal: listeners.signal });
        actions.append(control);
        return control;
      }
      previous = button(copy.previous, () => { page -= 1; });
      next = button(copy.next, () => { page += 1; });
      toggle = button(copy.showAll, () => { showAll = !showAll; page = 0; });
      const viewport = track.closest('.sd-native-reviews__viewport');
      if (!viewport || !viewport.parentNode) throw new Error('Native viewport unavailable');
      viewport.before(nav);
    }

    function sync() {
      if (failed) return;
      try {
        if (!main.matches(mainSelector) || !main.contains(wrapper)) { restore(); return; }
        const feeds = wrapper.querySelectorAll(feedSelector);
        const feed = feeds.length === 1 ? feeds[0] : null;
        const updatedTrack = feed && feed.querySelector('[data-sd-reviews-track]');
        const updatedCards = updatedTrack ? Array.from(updatedTrack.children) : [];
        if (updatedTrack === track && updatedCards.length === cards.length &&
            updatedCards.every((card, index) => card === cards[index]) && nav) return;
        const oldFocus = document.activeElement;
        const ownedFocus = !!nav && nav.contains(oldFocus);
        restore();
        track = updatedTrack;
        cards = updatedCards;
        page = 0;
        // Unknown/pre-filtered markup belongs to the native renderer. Leave it alone.
        if (!track || cards.length <= pageSize ||
            cards.some(card => !card.matches(cardSelector) || card.hidden)) return;
        const focusIndex = cards.findIndex(card => card.contains(oldFocus));
        if (focusIndex >= 0) page = Math.floor(focusIndex / pageSize);
        makeControls();
        render(ownedFocus);
      } catch (_) { failOpen(); }
    }

    const observer = new MutationObserver(mutations => {
      // Observe native track replacement/card population, not our status/button updates.
      const relevant = mutations.some(mutation => {
        const target = mutation.target;
        if (nav && nav.contains(target)) return false;
        if (mutation.type === 'attributes') return target.matches('.sd-native-reviews');
        if (target === track || (track && track.contains(target))) return true;
        return Array.from(mutation.addedNodes).concat(Array.from(mutation.removedNodes))
          .some(node => node.nodeType === 1 && !node.matches('.sd-review-pagination') &&
            (node.matches('.sd-native-reviews, [data-sd-reviews-track], .sd-native-reviews__viewport') ||
             node.querySelector('.sd-native-reviews, [data-sd-reviews-track]')));
      });
      if (relevant) sync();
    });
    observer.observe(wrapper, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-review-mode'] });
    sync();
    return () => { observer.disconnect(); restore(); };
  }

  function boot() {
    let copy;
    try { copy = labels(); } catch (_) { return; }
    if (!copy) return;
    for (const [main, cleanup] of managers) {
      if (!main.isConnected || !main.matches(mainSelector)) { cleanup(); managers.delete(main); }
    }
    for (const main of document.querySelectorAll(mainSelector)) {
      if (managers.has(main)) continue;
      const feeds = new Map();
      function refresh() {
        for (const [wrapper, cleanup] of feeds) {
          if (!main.matches(mainSelector) || !main.contains(wrapper)) { cleanup(); feeds.delete(wrapper); }
        }
        if (!main.matches(mainSelector)) return;
        for (const wrapper of main.querySelectorAll(wrapperSelector)) {
          if (!feeds.has(wrapper)) feeds.set(wrapper, attach(main, wrapper, copy));
        }
      }
      // Main's immediate sections only; each feed has its own focused native-DOM observer.
      const observer = new MutationObserver(refresh);
      observer.observe(main, { childList: true, attributes: true, attributeFilter: ['data-template'] });
      managers.set(main, () => {
        observer.disconnect();
        for (const cleanup of feeds.values()) cleanup();
        feeds.clear();
      });
      refresh();
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
  window.addEventListener('pageshow', boot);
  window.addEventListener('pagehide', () => {
    for (const cleanup of managers.values()) cleanup();
    managers.clear();
  });
})();
